import React, { useState, useEffect } from 'react';
import { Play, ArrowRight, Star, Users, Award, Clock } from 'lucide-react';
import LazyImage from '../common/LazyImage';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { targetRef, hasIntersected } = useIntersectionObserver();
  
  const slides = [
    {
      title: "Multi-specialty Physiotherapy Center in Indore",
      subtitle: "Comprehensive Rehab for Musculoskeletal, Neuro, Cardio, Sports & More",
      description: "Located at 55, Ambikapuri, 60 Feet Road, Aerodrome, Kalani Nagar, Raj Mohalla, Indore – 452005. Structured rehabilitation for all ages and conditions, led by Dr. Pragati Nayak & Dr. Deep Maheshwari.",
      image: "https://images.pexels.com/photos/4491459/pexels-photo-4491459.jpeg?auto=compress&cs=tinysrgb&w=800",
      cta: "Book Consultation"
    },
    {
      title: "Corporate Wellness Programs",
      subtitle: "Transforming Workplace Health",
      description: "Reduce absenteeism by 40% with our proven ergonomics and wellness solutions",
      image: "https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=800",
      cta: "Get Proposal"
    },
    {
      title: "Sports Performance & Recovery",
      subtitle: "Trusted by Athletes & Sports Teams",
      description: "Advanced techniques in sports physiotherapy, injury prevention, and performance enhancement",
      image: "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=800",
      cta: "Learn More"
    }
  ];

  const stats = [
    { icon: Users, number: "100+", label: "Patients Treated" },
    { icon: Award, number: "15+", label: "Years Experience" },
    { icon: Star, number: "4.9", label: "Patient Rating" },
    { icon: Clock, number: "24/7", label: "Emergency Care" }
  ];

  useEffect(() => {
    if (prefersReducedMotion) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [prefersReducedMotion]);

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  const handleCTAClick = (action: string) => {
    if (action === 'Book Consultation' || action === 'Get Proposal') {
      const contactSection = document.querySelector('#contact');
      contactSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home" 
      className="relative min-h-screen pt-16 sm:pt-20 scroll-mt-24 sm:scroll-mt-28 lg:scroll-mt-36"
      ref={targetRef}
      role="banner"
      aria-label="Hero section with clinic information"
    >
      {/* Hero Slider */}
      <div className="relative h-screen overflow-hidden" role="region" aria-label="Image carousel">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            role="tabpanel"
            aria-hidden={index !== currentSlide}
            aria-label={`Slide ${index + 1}: ${slide.title}`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30 z-10" aria-hidden="true"></div>
            <LazyImage
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              sizes="100vw"
            />
            
            {/* Text Content Container - Responsive and Contained */}
            <div className="absolute inset-0 z-20 flex items-center justify-center p-4 sm:p-6 lg:p-8" role="main">
              <div className="w-full max-w-4xl mx-auto">
                <div className="max-w-2xl mx-auto text-center lg:text-left">
                  {/* Title - Responsive with proper scaling */}
                  <h1 
                    className={`font-bold text-white mb-3 sm:mb-4 md:mb-6 leading-tight ${
                      hasIntersected && !prefersReducedMotion ? 'animate-fade-in-up' : ''
                    }`}
                    style={{
                      fontSize: 'clamp(1.125rem, 3vw, 3rem)',
                      lineHeight: '1.1',
                      maxWidth: '100%',
                      wordBreak: 'break-word'
                    }}
                    role="heading"
                    aria-level={1}
                  >
                    {slide.title}
                  </h1>
                  
                  {/* Subtitle - Responsive with proper scaling */}
                  <h2 
                    className={`text-blue-400 mb-2 sm:mb-3 md:mb-4 font-semibold ${
                      hasIntersected && !prefersReducedMotion ? 'animate-fade-in-up animation-delay-200' : ''
                    }`}
                    style={{
                      fontSize: 'clamp(0.75rem, 2vw, 1.25rem)',
                      lineHeight: '1.3',
                      maxWidth: '100%',
                      wordBreak: 'break-word'
                    }}
                    role="heading"
                    aria-level={2}
                  >
                    {slide.subtitle}
                  </h2>
                  
                  {/* Description - Responsive with proper scaling and word wrapping */}
                  <p 
                    className={`text-gray-200 mb-4 sm:mb-6 md:mb-8 leading-relaxed ${
                      hasIntersected && !prefersReducedMotion ? 'animate-fade-in-up animation-delay-400' : ''
                    }`}
                    style={{
                      fontSize: 'clamp(0.625rem, 1.5vw, 1rem)',
                      lineHeight: '1.5',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      wordBreak: 'break-word'
                    }}
                  >
                    {slide.description}
                  </p>
                  
                  {/* CTA Buttons - Responsive and properly contained */}
                  <div 
                    className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start ${
                      hasIntersected && !prefersReducedMotion ? 'animate-fade-in-up animation-delay-600' : ''
                    }`}
                    role="group"
                    aria-label="Call to action buttons"
                  >
                    <button 
                      className="group bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center focus:ring-2 focus:ring-blue-300 focus:outline-none w-full sm:w-auto shadow-lg hover:shadow-xl"
                      style={{
                        fontSize: 'clamp(0.625rem, 1.5vw, 0.875rem)',
                        minWidth: 'fit-content'
                      }}
                      onClick={() => handleCTAClick(slide.cta)}
                      aria-label={`${slide.cta} - Navigate to contact section`}
                    >
                      {slide.cta}
                      <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button 
                      className="group border-2 border-white text-white hover:bg-white hover:text-gray-900 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center focus:ring-2 focus:ring-white focus:outline-none w-full sm:w-auto shadow-lg hover:shadow-xl"
                      style={{
                        fontSize: 'clamp(0.625rem, 1.5vw, 0.875rem)',
                        minWidth: 'fit-content'
                      }}
                      aria-label="Watch promotional video"
                    >
                      <Play className="mr-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                      Watch Video
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Slide Indicators */}
      <div 
        className="absolute bottom-16 sm:bottom-20 left-1/2 transform -translate-x-1/2 z-30 flex space-x-3"
        role="tablist"
        aria-label="Slide navigation"
      >
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => handleSlideChange(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-blue-400 w-8' : 'bg-white/50'
            }`}
            role="tab"
            aria-selected={index === currentSlide}
            aria-label={`Go to slide ${index + 1}`}
            tabIndex={0}
          />
        ))}
      </div>

      {/* Stats Section */}
      <div 
        className="absolute bottom-0 w-full bg-white/95 backdrop-blur-sm z-30 hidden sm:block"
        role="region"
        aria-label="Clinic statistics"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center group"
                role="group"
                aria-label={`${stat.number} ${stat.label}`}
              >
                <div className="flex justify-center mb-3">
                  <stat.icon 
                    className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform duration-300" 
                    aria-hidden="true"
                  />
                </div>
                <div 
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1"
                  role="text"
                  aria-label={`Statistic: ${stat.number}`}
                >
                  {stat.number}
                </div>
                <div 
                  className="text-sm text-gray-600"
                  role="text"
                  aria-label={stat.label}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;